/* eslint-disable camelcase */
import * as React from 'react';
import { memo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';

// MUI
import {
    Autocomplete,
    Button,
    FormControl,
    Grid2 as Grid,
    Link as MuiLink,
    Stack,
    TextField,
    Tooltip,
    Typography,
    Switch,
    Chip,
    useMediaQuery
} from '@mui/material';
import { Box, width } from '@mui/system';
import { Info } from '@mui/icons-material';

// Hooks
import useAuth from 'hooks/useAuth';
import useSnackbar from 'hooks/useSnackbar';
import usePermission from 'hooks/usePermission';
import { v4 as uuidv4 } from 'uuid';
// Constants and Utils
import { encriptId } from 'utils/encription';
import {
    GLOBAL_DATE_FORMAT,
    LIST_LIMIT,
    SERVICE_ADDT,
    SERVICE_ADDT_ANCILLARY,
    SERVICE_COUNSELING,
    SERVICE_EIDT,
    SERVICE_EIDT_ANCILLARY,
    SERVICE_IEP,
    SERVICE_NURSING,
    SERVICE_PERSONAL_CARE,
    SERVICE_PERSONAL_CARE_ON_BUS,
    SERVICE_PSYCHOLOGICAL_SERVICE,
    SERVICE_PSYCHOLOGICAL_TESTING,
    SERVICE_TRANSPORTATION
} from 'constant';

// API
import { useLazyGetZeroClaimReportsQuery, useLazyDownloadZeroClaimsReportsQuery } from 'store/services/reports/zeroClaimReport';
import { useGetLocationCustomListQuery } from 'store/services/location';

// Components
import * as yup from 'yup';
import { useFormik } from 'formik';
import MainCard from 'ui-component/cards/MainCard';
import CTable from 'components/CTable';
import NoData from 'components/reusable/NoData';
import ExportColumnData from 'components/ExportColumnData';
import { ServiceDropdown } from 'components/reusable/Dropdowns';
import { AspyreDatePicker as DatePicker } from 'components/common/InputFields';

// Schema
const validationSchema = yup.object({
    locations: yup.array().nullable().typeError('Please enter a valid location'),

    services: yup.array().nullable().typeError('Please enter a valid service')
    // startDate: yup.date().nullable().required('Start date is required').typeError('Please enter a valid start date'),

    // endDate: yup
    //     .date()
    //     .nullable()
    //     .required('End date is required')
    //     .typeError('Please enter a valid end date')
    //     .min(yup.ref('startDate'), 'End date cannot be before start date')
});

const defaultColumnProps = { flex: 1, sortable: false };

const cols = (nameHeader, isNarrowScreen) => [
    {
        ...defaultColumnProps,
        field: 'name',
        headerName: nameHeader,
        sortable: true,
        flex: 0,
        width: 250,
        renderCell: (params) => (
            <Tooltip title={params.value}>
                <Typography
                    variant="body2"
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                        width: '100%'
                    }}
                >
                    {params.value}
                </Typography>
            </Tooltip>
        )
    },
    {
        ...defaultColumnProps,
        field: 'locations',
        headerName: 'Location',
        minWidth: 160,
        sortable: false,
        renderCell: (params) => {
            const locations = (params.row.locations || '').split(';').filter(Boolean);
            const maxVisible = isNarrowScreen ? 1 : 2;
            return (
                <Stack direction="row" spacing={1}>
                    {locations.slice(0, maxVisible)?.map((item) => (
                        <Tooltip title={item}>
                            <Chip style={{ width: 160, wordBreak: 'break-all' }} variant="outlined" label={item} />
                        </Tooltip>
                    ))}
                    {locations?.length > maxVisible && (
                        <Tooltip
                            title={locations
                                ?.slice(maxVisible)
                                .map((item) => item)
                                .join(', ')}
                        >
                            <Chip
                                style={{ width: 105, wordBreak: 'break-all' }}
                                variant="outlined"
                                label={`+${locations?.length - maxVisible} more`}
                            />
                        </Tooltip>
                    )}
                </Stack>
            );
        }
    },
    {
        field: 'services',
        headerName: 'Service',
        ...defaultColumnProps,
        renderCell: (params) => {
            const services = (params.row.services || '').split(',').filter(Boolean);
            const maxVisible = isNarrowScreen ? 1 : 2;
            return (
                <Stack direction="row" spacing={1}>
                    {services.slice(0, maxVisible)?.map((item) => (
                        <Tooltip title={item}>
                            <Chip style={{ width: 155, wordBreak: 'break-all' }} variant="outlined" label={item} />
                        </Tooltip>
                    ))}
                    {services?.length > maxVisible && (
                        <Tooltip
                            title={services
                                ?.slice(maxVisible)
                                .map((item) => item)
                                .join(', ')}
                        >
                            <Chip
                                style={{ width: 105, wordBreak: 'break-all' }}
                                variant="outlined"
                                label={`+${services?.length - maxVisible} more`}
                            />
                        </Tooltip>
                    )}
                </Stack>
            );
        }
    }
];

const DEFAULT_SORT_ORDER = 'asc';
const DEFAULT_SORT_COLUMN = 'name';

const ZeroClaimReport = () => {
    const { user } = useAuth();
    const { notify } = useSnackbar();
    const { hasPermission } = usePermission();
    const isNarrowScreen = useMediaQuery('(max-width:1359.95px)');

    const [page, setPage] = useState(1);
    const [exportData, setExportData] = useState(null);
    const [reports, setReports] = useState({ fields: [] });

    const [nameHeader, setNameHeader] = useState(null);
    const [orderby, setOrderBy] = useState(DEFAULT_SORT_COLUMN);
    const [order, setOrder] = useState(DEFAULT_SORT_ORDER);
    const defaultInitialState = { field: DEFAULT_SORT_COLUMN, sort: DEFAULT_SORT_ORDER };
    const formik = useFormik({
        initialValues: {
            type: 'therapist',
            locations: [],
            services: []
            // startDate: null,
            // endDate: null
        },
        validationSchema
    });

    console.log('formik::', formik);

    const { data: locationData, isLoading: locationIsLoading } = useGetLocationCustomListQuery(
        {
            payload: {
                filters: {
                    status: 1,
                    ...(user?.client_id && { client_id: user.client_id }),
                    order_by: 'name',
                    order: 'asc'
                }
            },
            subType: 'LIST_DROPDOWN'
        },
        { skip: !user?.client_id }
    );

    const [trigger, { data: reportData, isFetching: reportLoading }] = useLazyGetZeroClaimReportsQuery();
    const [downloadReport, { isFetching: reportDownloading }] = useLazyDownloadZeroClaimsReportsQuery();

    const filters = {
        client_id: user?.client_id,
        location_id: (formik.values.locations || []).map((loc) => String(loc.id ?? '')),
        service_id: (formik.values.services || []).map((serv) => String(serv.id ?? '')),
        is_therapist: formik.values.type === 'therapist' ? 1 : 0,
        current_page: page,
        is_download: 0,
        order_by: orderby,
        order_type: order
        // ...(formik.values.startDate && {
        //     start_date: moment(new Date(formik.values.startDate)).startOf('day').utc().format('YYYY-MM-DD HH:mm:ss')
        // }),
        // ...(formik.values.endDate && {
        //     end_date: moment(new Date(formik.values.endDate)).endOf('day').utc().format('YYYY-MM-DD HH:mm:ss')
        // })
    };

    useEffect(() => {
        if (reportData && !reportLoading) setReports(reportData);
    }, [reportData, reportLoading]);

    useEffect(() => {
        setPage(1);
    }, [formik.values]);

    const handleSearch = async () => {
        const errors = await formik.validateForm();
        if (Object.keys(errors).length === 0) {
            setPage(1);
            setNameHeader(formik.values?.type === 'therapist' ? 'Therapist Name' : 'Patient Name');
            trigger({ ...filters, current_page: 1 });
        }
    };

    const handleResetForm = () => {
        formik.resetForm();
        setReports({ fields: [] });
    };

    const handleDownload = async () => {
        try {
            const res = await downloadReport({ ...filters, is_download: 1 }).unwrap();
            setExportData(res);
        } catch (error) {
            notify({ message: 'Error downloading report', severity: 'error' });
        }
    };

    const handleSortModelChange = async (sortModel) => {
        const errors = await formik.validateForm();
        if (Object.keys(errors).length === 0) {
            setPage(1);
            const { field, sort } = sortModel[0] || {};
            setOrderBy(field || DEFAULT_SORT_COLUMN);
            setOrder(sort || DEFAULT_SORT_ORDER);

            trigger({ ...filters, current_page: 1, order_by: field || DEFAULT_SORT_COLUMN, order_type: sort || DEFAULT_SORT_ORDER });
        }
    };

    return (
        <MainCard
            title={
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                        alignItems: 'center'
                    }}
                >
                    <Typography variant="h3">Zero Claim Report</Typography>
                    <Tooltip
                        enterTouchDelay={0}
                        title="The report displays a list of therapists or patients who have not had any paid claims or invoices submitted within the specified date range."
                    >
                        <Info sx={{ cursor: 'pointer', height: 20, width: 20, color: (theme) => theme.palette.grey[400] }} />
                    </Tooltip>
                </Stack>
            }
            isLoading={reportDownloading || reportLoading}
        >
            <Grid container spacing={1}>
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                    <FormControl fullWidth>
                        <Autocomplete
                            value={formik.values.locations}
                            multiple
                            limitTags={2}
                            loading={locationIsLoading}
                            options={locationData?.fields || []}
                            getOptionLabel={(option) => option?.name || ''}
                            onChange={(_e, val) => formik.setFieldValue('locations', val)}
                            renderOption={(props, option) => (
                                <li {...props} style={{ color: !option?.is_active && '#959494' }}>
                                    {option?.name || ''}
                                    {!option?.is_active ? ' (Inactive)' : ''}
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Location"
                                    error={Boolean(formik.errors.locations)}
                                    helperText={formik.errors.locations}
                                />
                            )}
                        />
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                    <FormControl fullWidth>
                        <ServiceDropdown
                            limitTags={2}
                            multiple
                            async
                            value={formik.values.services}
                            excludes={[
                                SERVICE_EIDT,
                                SERVICE_ADDT,
                                SERVICE_IEP,
                                SERVICE_EIDT_ANCILLARY,
                                SERVICE_ADDT_ANCILLARY,
                                SERVICE_PERSONAL_CARE,
                                SERVICE_PERSONAL_CARE_ON_BUS,
                                SERVICE_TRANSPORTATION,
                                SERVICE_PSYCHOLOGICAL_TESTING,
                                SERVICE_COUNSELING,
                                SERVICE_NURSING,
                                SERVICE_PSYCHOLOGICAL_SERVICE
                            ]}
                            onChange={(v) => formik.setFieldValue('services', v)}
                            error={Boolean(formik.errors.services)}
                            helperText={formik.errors.services}
                        />
                    </FormControl>
                </Grid>

                {/* <Grid size={{ xs: 12, sm: 6, md: 4, lg: 1.75 }}>
                    <FormControl fullWidth>
                        <DatePicker
                            label="Start Date"
                            inputFormat={GLOBAL_DATE_FORMAT}
                            value={formik.values.startDate}
                            onChange={(v) => formik.setFieldValue('startDate', v)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    required
                                    variant="outlined"
                                    label="Start Date"
                                    error={Boolean(formik.errors.startDate)}
                                    helperText={formik.errors.startDate}
                                    inputProps={{ ...params.inputProps }}
                                />
                            )}
                        />
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 1.75 }}>
                    <FormControl fullWidth>
                        <DatePicker
                            label="End Date"
                            minDate={formik.values.startDate}
                            inputFormat={GLOBAL_DATE_FORMAT}
                            value={formik.values.endDate}
                            onChange={(v) => formik.setFieldValue('endDate', v)}
                            renderInput={(params) => (
                                <TextField
                                    required
                                    {...params}
                                    variant="outlined"
                                    label="End Date"
                                    error={Boolean(formik.errors.endDate)}
                                    helperText={formik.errors.endDate}
                                    inputProps={{ ...params.inputProps }}
                                />
                            )}
                        />
                    </FormControl>
                </Grid> */}

                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                    <FormControl fullWidth>
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 1
                            }}
                        >
                            <Typography
                                sx={{
                                    marginLeft: 1
                                }}
                            >
                                Therapist{' '}
                            </Typography>
                            <Switch
                                checked={formik.values.type === 'patient'}
                                onChange={(e) => formik.setFieldValue('type', e.target.checked ? 'patient' : 'therapist')}
                            />
                            <Typography
                                sx={{
                                    marginRight: 1
                                }}
                            >
                                Patient
                            </Typography>
                        </Stack>
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                    <Stack direction="row" spacing={1} sx={{ minHeight: 50 }}>
                        <Button onClick={handleSearch} variant="contained">
                            Search
                        </Button>
                        <Button onClick={handleResetForm} variant="contained">
                            Reset
                        </Button>
                        {reports?.fields?.length > 0 && hasPermission('zero_claim_report_download') && (
                            <ExportColumnData
                                handleDownload={handleDownload}
                                handleClose={() => setExportData(null)}
                                exportData={exportData}
                                betaBtn
                            />
                        )}
                    </Stack>
                </Grid>
            </Grid>
            {!reports?.fields?.length ? (
                <NoData title="No Data Found" />
            ) : (
                <Box sx={{ height: 'calc(100vh - 280px)', mt: 2, overflowX: 'auto' }}>
                    <CTable
                        rows={reports?.fields || []}
                        cols={cols(nameHeader, isNarrowScreen)}
                        getRowId={() => uuidv4()}
                        currentPage={page}
                        totalCount={reports?.fields?.[0]?.total_count}
                        countPerPage={LIST_LIMIT}
                        handleSortModelChange={handleSortModelChange}
                        onPageChange={(_, val) => {
                            setPage(val);
                            trigger({ ...filters, current_page: val });
                        }}
                        initialState={defaultInitialState}
                        sx={{ minWidth: 998 }}
                    />
                </Box>
            )}
        </MainCard>
    );
};

export default memo(ZeroClaimReport);
